import { useEffect, useState } from "react";
import { Zap, ZapOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-white border border-gray-300 rounded-lg text-sm px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#E80071]/30 focus:border-[#E80071]";

export const FlashSaleCard = ({ brands, onApplied }) => {
  const [categories, setCategories] = useState([]);
  const [targetType, setTargetType] = useState("category");
  const [target, setTarget] = useState("skincare");
  const [percent, setPercent] = useState("20");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const run = async (path, successMsg) => {
    setBusy(true);
    try {
      const { data } = await api.post(path, {
        target_type: targetType,
        target: targetType === "all" ? "" : target,
        percent: Number(percent),
        start: start ? new Date(start).toISOString() : "",
        end: end ? new Date(end).toISOString() : "",
      });
      toast.success(successMsg(data));
      onApplied?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5" data-testid="flash-sale-card">
      <p className="text-sm font-medium text-[#111827] mb-1 flex items-center gap-2"><Zap size={15} className="text-[#E80071]" /> Flash Sale</p>
      <p className="text-xs text-gray-400 mb-4">Drop prices across a whole category or brand in one click. The sale price, ribbon and countdown switch on automatically — and switch off when the window ends.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <select value={targetType} onChange={(e) => { setTargetType(e.target.value); setTarget(e.target.value === "brand" ? brands[0]?.slug || "" : "skincare"); }} data-testid="flash-target-type" className={inputCls}>
          <option value="category">A category</option>
          <option value="brand">A brand</option>
          <option value="all">Entire store</option>
        </select>
        {targetType === "category" && (
          <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="flash-target-cat" className={inputCls}>
            {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name} ({c.count})</option>)}
          </select>
        )}
        {targetType === "brand" && (
          <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="flash-target-brand" className={inputCls}>
            {brands.map((b) => <option key={b.slug} value={b.slug}>{b.name}</option>)}
          </select>
        )}
        <div className="flex items-center gap-2">
          <input type="number" min="1" max="89" value={percent} onChange={(e) => setPercent(e.target.value)} data-testid="flash-percent" className={inputCls} />
          <span className="text-sm text-gray-500 shrink-0">% off</span>
        </div>
        <input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} data-testid="flash-start" className={inputCls} title="Sale starts" />
        <input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="flash-end" className={inputCls} title="Sale ends" />
      </div>
      <div className="flex gap-3 mt-4">
        <button
          onClick={() => run("/admin/flash-sale", (d) => `Flash sale live — ${d.updated} products at ${d.percent}% off`)}
          disabled={busy || !start || !end}
          data-testid="flash-apply-btn"
          className="flex items-center gap-2 bg-[#E80071] hover:bg-[#D00065] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm disabled:opacity-50"
        >
          <Zap size={14} /> {busy ? "Applying…" : "Start flash sale"}
        </button>
        <button
          onClick={() => { if (window.confirm("End the flash sale for this selection? Prices return to normal immediately.")) run("/admin/flash-sale/clear", (d) => `Flash sale ended — ${d.cleared} products back to regular price`); }}
          disabled={busy}
          data-testid="flash-clear-btn"
          className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm disabled:opacity-50"
        >
          <ZapOff size={14} /> End sale now
        </button>
      </div>
    </div>
  );
};
